import { ctvCompletion } from "../ctv/CtvParts";
const fmt = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return Math.round(n).toLocaleString();
};

const Hi = ({ children }) => <span className="text-lime-300 font-semibold">{children}</span>;

const multiple = (clicks, imp, bench) => {
  if (!imp || !clicks) return null;
  const m = ((clicks / imp) * 100) / bench;
  if (m < 1.2) return null;
  return m >= 10 ? Math.round(m) : m.toFixed(1);
};

const clickTail = (clicks, imp, bench) => {
  const m = multiple(clicks, imp, bench);
  if (m) return <>, and they clicked at <Hi>{m}x the industry average</Hi></>;
  if (clicks > 0) return <>, with <Hi>{fmt(clicks)}</Hi> clicking through to learn more</>;
  return null;
};

// One short story + two key numbers per channel. Returns only channels with data.
export const buildChannelStories = ({
  clientName, metaOverview, mobileOverview, smOverview, tiktokOverview, googleSummary, pmax, appfrontSummary, ctvPublishers = [],
}) => {
  const out = {};

  const mImp = metaOverview?.impressions || metaOverview?.total_impressions || 0;
  const mClicks = metaOverview?.clicks || metaOverview?.total_clicks || 0;
  const mReach = metaOverview?.reach || 0;
  if (mImp > 0) {
    out.meta = {
      name: "Facebook & Instagram", clicks: mClicks, impressions: mImp,
      sentence: mReach
        ? <>Facebook & Instagram put {clientName} in front of <Hi>{fmt(mReach)} people</Hi>{clickTail(mClicks, mImp, 0.9)}.</>
        : <>Facebook & Instagram showed {clientName} <Hi>{fmt(mImp)} times</Hi>{clickTail(mClicks, mImp, 0.9)}.</>,
      stats: [[mReach ? "People reached" : "Times seen", fmt(mReach || mImp)], ["Clicks", fmt(mClicks)]],
    };
  }

  const moImp = mobileOverview?.total_impressions || 0;
  const moClicks = mobileOverview?.total_clicks || 0;
  if (moImp > 0) {
    out.mobile = {
      name: "Mobile ads", clicks: moClicks, impressions: moImp,
      sentence: <>Mobile ads showed {clientName} on phones <Hi>{fmt(moImp)} times</Hi>, right where people browse every day{clickTail(moClicks, moImp, 0.07)}.</>,
      stats: [["Times seen", fmt(moImp)], ["Clicks", fmt(moClicks)]],
    };
  }

  const sImp = smOverview?.total_impressions || 0;
  const sClicks = smOverview?.total_clicks || 0;
  if (sImp > 0) {
    out.socialMirror = {
      name: "Social Mirror", clicks: sClicks, impressions: sImp,
      sentence: <>Social Mirror carried your social posts onto popular websites <Hi>{fmt(sImp)} times</Hi>{clickTail(sClicks, sImp, 0.07)}.</>,
      stats: [["Times seen", fmt(sImp)], ["Clicks", fmt(sClicks)]],
    };
  }

  const tImp = tiktokOverview?.impressions || 0;
  const tClicks = tiktokOverview?.clicks || 0;
  const tReach = tiktokOverview?.reach || 0;
  if (tImp > 0) {
    out.tiktok = {
      name: "TikTok", clicks: tClicks, impressions: tImp,
      sentence: <>On TikTok, {clientName} reached <Hi>{fmt(tReach || tImp)} {tReach ? "people" : "screens"}</Hi> in the feed where they spend their downtime{tClicks > 0 ? <>, earning <Hi>{fmt(tClicks)} clicks</Hi></> : null}.</>,
      stats: [[tReach ? "People reached" : "Times seen", fmt(tReach || tImp)], ["Clicks", fmt(tClicks)]],
    };
  }

  const gImp = googleSummary?.total_impressions || 0;
  const gClicks = googleSummary?.total_clicks || 0;
  const gConv = googleSummary?.total_conversions || 0;
  if (gImp > 0) {
    out.googleAds = {
      name: "Google Search", clicks: gClicks, impressions: gImp,
      sentence: <>When people searched on Google, {clientName} showed up <Hi>{fmt(gImp)} times</Hi> and <Hi>{fmt(gClicks)}</Hi> clicked through{gConv > 0 ? <>, leading to <Hi>{fmt(gConv)} actions</Hi> like calls and form fills</> : null}.</>,
      stats: [["Clicks", fmt(gClicks)], gConv > 0 ? ["Actions taken", fmt(gConv)] : ["Times seen", fmt(gImp)]],
    };
  }

  if (pmax?.impressions > 0) {
    out.pmax = {
      name: "Google Performance Max", clicks: pmax.clicks, impressions: pmax.impressions,
      sentence: <>Google's AI placed {clientName} across Search, YouTube, Maps and Gmail <Hi>{fmt(pmax.impressions)} times</Hi>{pmax.clicks > 0 ? <>, earning <Hi>{fmt(pmax.clicks)} clicks</Hi></> : null}.</>,
      stats: [["Times seen", fmt(pmax.impressions)], ["Clicks", fmt(pmax.clicks)]],
    };
  }

  const delivered = (appfrontSummary?.total_email_delivered || 0) + (appfrontSummary?.total_push_delivered || 0);
  if (delivered > 0) {
    const open = appfrontSummary?.email_open_rate || 0;
    const redeemed = appfrontSummary?.total_redeemed || 0;
    out.appfront = {
      name: "App Front", clicks: appfrontSummary?.total_email_clicked || 0, impressions: delivered,
      sentence: <>Your app and email messages landed <Hi>{fmt(delivered)} times</Hi> with people who already know you{open > 0 ? <>, and <Hi>{Math.round(open)}%</Hi> opened your emails</> : null}.</>,
      stats: [["Messages delivered", fmt(delivered)], redeemed > 0 ? ["Offers redeemed", fmt(redeemed)] : ["Email open rate", `${Math.round(open)}%`]],
    };
  }

  const cImp = ctvPublishers.reduce((s, p) => s + (p.impressions || 0), 0);
  if (cImp > 0) {
    const comp = ctvCompletion(ctvPublishers);
    const platforms = ctvPublishers.filter((p) => !p.is_other).length;
    out.ctv = {
      name: "Streaming TV", clicks: 0, impressions: cImp,
      sentence: <>Streaming TV ads played <Hi>{fmt(cImp)} times</Hi>{platforms ? <> on {platforms} platforms</> : null}{comp > 0 ? <>, and <Hi>{Math.round(comp)}%</Hi> of viewers watched to the very end</> : null}.</>,
      stats: [["Times seen", fmt(cImp)], ...(comp > 0 ? [["Watched to the end", `${Math.round(comp)}%`]] : [])],
    };
  }

  return out;
};

export const pickStarChannel = (stories) => {
  const list = Object.values(stories);
  if (list.length < 2) return null;
  const byClicks = [...list].sort((a, b) => b.clicks - a.clicks)[0];
  if (byClicks.clicks > 0) return { name: byClicks.name, what: "brought the most clicks", value: `${fmt(byClicks.clicks)} clicks` };
  const byImp = [...list].sort((a, b) => b.impressions - a.impressions)[0];
  return { name: byImp.name, what: "showed you most often", value: `${fmt(byImp.impressions)} times seen` };
};
